// @ts-expect-error TS6133 -- MIGRATION: not approved, fix when encountered
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createSearchParams, useNavigate } from 'react-router-dom'
import { useDeviceData, osName } from 'react-device-detect'
import {
  Button,
  Field,
  FormLabel,
  Heading,
  Input,
  LoadingSection,
  Paragraph,
  PasswordVisibilityButton,
  showToast,
} from '@quickmd-llc/design-system'
import { getEnv, getVersion } from '../../utils/buildinfo'
import { resetAdminProfile } from '../../store/accountSettings/AccountSettingsActions'
import {
  FORGOT_PASSWORD_PAGE_PATH,
  LOCAL_STORAGE_STATE_TO_PAST_VISIT,
  MANAGEMENT_PAGE_ROUTE,
} from '../../utils/constants'
import { setError } from '@/store/home/HomeActions'
import { login } from '@/store/home/HomeThunk'
import { LOGIN_SUCCESS } from '@/store/home/HomeConstants'
import { useStytchOAuth } from '@/hooks/useStytchOAuth'
import { errorCodes, getUserFriendlyErrorMessage } from '../../utils/errormessages'
import { validateEmail } from '../../utils/common'
import logo from '../../images/common/quickmd-main-logo.png'
import googleIcon from '../../images/common/google-icon.svg'
import { LOGOUT } from '@/app/authState'

export default function Home() {
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const loginSuccess = useSelector((state) => state.auth.accessToken)
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const error = useSelector((state) => state.home.error)
  // @ts-expect-error TS18046 -- MIGRATION: not approved, fix when encountered
  const useGoogleSSO = useSelector((state) => state.flags.flags.admin_google_sso)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
  const { browser } = useDeviceData(typeof navigator !== 'undefined' ? navigator.userAgent : undefined)

  const [emailValue, setEmailValue] = useState('')
  const [passwordValue, setPasswordValue] = useState('')
  const [isEmailError, setEmailError] = useState(false)
  const [isPasswordError, setPasswordError] = useState(false)
  const [isPasswordVisible, setIsPasswordVisible] = useState(false)

  const clientInfo = useMemo(
    () => ({
      OsType: osName,
      Type: browser.name,
      Version: browser.version,
    }),
    [browser.name, browser.version],
  )

  function navigateToManagement() {
    // @ts-expect-error TS2769 -- MIGRATION: not approved, fix when encountered
    navigate({
      replace: true,
      pathname: MANAGEMENT_PAGE_ROUTE,
      search: createSearchParams({
        IsDoctor: 'true',
        IsActive: 'true',
      }).toString(),
    })
  }

  const { startGoogleOAuth, isVerifying } = useStytchOAuth({
    // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
    onSuccess: (response) => {
      dispatch({ type: LOGIN_SUCCESS, payload: { AccessToken: response.AccessToken } })
      navigateToManagement()
    },
    onFailure: () => {
      dispatch({ type: LOGOUT })
      showToast({
        type: 'error',
        message: 'Authentication failed. Please contact support.',
      })
    },
  })

  useEffect(() => {
    localStorage.removeItem(LOCAL_STORAGE_STATE_TO_PAST_VISIT)
    dispatch(resetAdminProfile())
    if (loginSuccess) {
      navigateToManagement()
    }
  }, [loginSuccess])

  const onSetEmail = useCallback(
    // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
    (value) => {
      setEmailValue(value)
      setEmailError(false)
      if (error) dispatch(setError())
    },
    [dispatch, error],
  )

  const onSetPassword = useCallback(
    // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
    (value) => {
      setPasswordValue(value)
      setPasswordError(false)
      if (error) dispatch(setError())
    },
    [dispatch, error],
  )

  const onLogin = useCallback(() => {
    setPasswordError(false)
    setEmailError(false)
    let isError = false
    if (!emailValue.trim() && !passwordValue.trim()) {
      // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
      dispatch(setError(errorCodes.FILL_ALL_FIELDS))
      isError = true
      setEmailError(isError)
      setPasswordError(true)
    } else {
      if (!emailValue.trim()) {
        // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
        dispatch(setError(errorCodes.FIELD_REQUIRED, 'Email'))
        isError = true
        setEmailError(isError)
      } else if (!validateEmail(emailValue)) {
        // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
        dispatch(setError(errorCodes.ENTER_VALID, 'email'))
        isError = true
        setEmailError(isError)
      }
      if (!passwordValue.trim()) {
        // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
        if (!isError) dispatch(setError(errorCodes.FIELD_REQUIRED, 'Password'))
        isError = true
        setPasswordError(true)
      }
    }
    if (!isError) {
      dispatch(login({ Email: emailValue, Password: passwordValue, ClientInfo: clientInfo }))
    }
  }, [clientInfo, dispatch, emailValue, passwordValue])

  const onSubmit = useCallback(
    // @ts-expect-error TS7006 -- MIGRATION: not approved, fix when encountered
    (e) => {
      e.preventDefault()
      onLogin()
    },
    [onLogin],
  )

  const onForgotPassword = useCallback(() => {
    if (error) dispatch(setError())
    setPasswordError(false)
    setEmailError(false)
    navigate(FORGOT_PASSWORD_PAGE_PATH)
  }, [dispatch, error, navigate])

  const onContinueWithGoogle = () => {
    try {
      startGoogleOAuth()
    } catch (e) {
      console.error('Error during Google login:', e)
    }
  }

  const isLoginError = error === getUserFriendlyErrorMessage(errorCodes.INCORRECT_LOGIN)

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <div className='relative flex min-h-screen w-full flex-col items-center justify-center bg-white'>
        <div className='bg-surface flex w-sp-80 max-w-[90vw] flex-col items-center gap-sp-3 rounded-large border border-border-medium px-sp-3 py-sp-4'>
          <img src={logo} alt='QuickMD Logo' className='h-auto w-[213px]' />
          {isVerifying ?
            <div className='flex w-full flex-col items-center gap-sp-2 py-sp-4' aria-live='polite'>
              <LoadingSection />
              <p className='m-0 text-center text-text-light'>Signing you in…</p>
            </div>
          : <>
              <Heading level='h2' className='m-0 text-center'>
                Sign in
              </Heading>

              {!useGoogleSSO && (
                <form onSubmit={onSubmit} noValidate className='flex w-full flex-col items-center gap-sp-3'>
                  {/* Form-level error banner. Must NOT use <ErrorMessage> here: that's a
                      Headless UI <Description>, which throws unless it's inside a <Field>.
                      A standalone <Paragraph> renders the same error text without that crash. */}
                  {error ?
                    <Paragraph size='sm' error role='alert' aria-live='polite' className='w-full text-center'>
                      {error}
                    </Paragraph>
                  : null}
                  <Field className='w-full' error={isEmailError || isLoginError}>
                    <FormLabel>Email</FormLabel>
                    <Input
                      type='email'
                      autoComplete='email'
                      placeholder='Enter your email'
                      value={emailValue}
                      onChange={(e) => onSetEmail(e.target.value)}
                      error={isEmailError || isLoginError}
                    />
                  </Field>
                  <Field className='w-full' error={isPasswordError || isLoginError}>
                    <FormLabel>Password</FormLabel>
                    <Input
                      type={isPasswordVisible ? 'text' : 'password'}
                      autoComplete='current-password'
                      placeholder='Enter your password'
                      value={passwordValue}
                      onChange={(e) => onSetPassword(e.target.value)}
                      error={isPasswordError || isLoginError}
                      rightSideLabel={
                        <PasswordVisibilityButton
                          isVisible={isPasswordVisible}
                          onClick={() => setIsPasswordVisible((v) => !v)}
                        />
                      }
                    />
                  </Field>
                  <Button priority='primary' type='submit' className='w-full'>
                    Sign In
                  </Button>
                  <button
                    type='button'
                    onClick={onForgotPassword}
                    className='cursor-pointer border-0 bg-transparent p-0 text-sm text-text-light underline'>
                    Forgot password?
                  </button>
                </form>
              )}

              <button
                type='button'
                className="bg-surface flex w-full cursor-pointer items-center justify-center gap-sp-1 rounded-full border border-[#E7E7E7] px-sp-2 py-sp-1.5 font-['Inter'] text-sm font-semibold text-[#121212] transition-colors hover:bg-[#F0F6FF] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4285F4]"
                onClick={onContinueWithGoogle}>
                <img src={googleIcon} alt='' className='h-sp-2.5 w-sp-2.5 shrink-0' />
                Continue with Google
              </button>
            </>
          }
        </div>
        <span className='absolute bottom-sp-3 right-sp-3 text-sm text-text-light'>
          {`v${getVersion()}(${getEnv()})`}
        </span>
      </div>
    </div>
  )
}
